import { ReactNode } from 'react';

import { Typography, styled } from '@material-ui/core';

import { add } from 'date-fns';
import * as duration from 'duration-fns';

import {
  ResourceLink,
  Timeline,
  extractWorkflowFromInstanceName,
} from '@spotify/confidence-core-react';

import { WorkflowInstanceListItem } from '../../hooks/useListWorkflowInstances';
import { workflowInstanceRouteRef } from '../../routes';
import { StatusIndicator } from '../StatusIndicator';
import {
  WorkflowInstanceStateType,
  useWorkflowInstanceState,
} from '../WorkflowInstanceState';

const maybeDate = (date: string | undefined | null) =>
  date ? new Date(date) : undefined;

export const extractWorkflowTimelineData = (item: WorkflowInstanceListItem) => {
  const data = item.moduleData2.planning;

  const rolloutSchedule = item?.moduleData2?.rollout?.schedule;

  let plannedEnd = maybeDate(data?.planned?.endTime);
  const actualStart = maybeDate(data?.actual?.startTime);

  if (rolloutSchedule && actualStart && !plannedEnd) {
    if (
      rolloutSchedule.state &&
      ['ENABLED', 'COMPLETED'].includes(rolloutSchedule.state) &&
      rolloutSchedule.steps.length > 0
    ) {
      plannedEnd = rolloutSchedule?.steps.reduce((acc, step) => {
        return add(acc, duration.parse(step.durationSincePreviousStep));
      }, new Date(actualStart));
    }
  }

  return {
    start: actualStart,
    end: maybeDate(data?.actual?.endTime),
    plannedStart: maybeDate(data?.planned?.startTime),
    plannedEnd: plannedEnd,
    item,
  };
};

const Wrapper = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const WorkflowInstanceItem = (item: WorkflowInstanceListItem) => {
  const { status, label } = useWorkflowInstanceState({
    state: item.state as WorkflowInstanceStateType,
    checks: item.checks,
    pendingTransition: item.pendingTransition,
  });

  return (
    <Wrapper>
      <StatusIndicator
        status={status}
        label={label}
        hasStarted={!!extractWorkflowTimelineData(item)?.start}
      />
      <Typography variant="body2">{item.displayName}</Typography>
    </Wrapper>
  );
};

export const WorkflowInstanceTimeline = ({
  items,
  children,
  topOffset,
  loading,
}: {
  topOffset?: number;
  items: WorkflowInstanceListItem[];
  children?: ReactNode;
  loading?: boolean;
}) => {
  return (
    <>
      <Timeline
        items={items}
        extract={extractWorkflowTimelineData}
        topOffset={topOffset}
        getLabel={item => (
          <ResourceLink
            name={item.name}
            routeRef={workflowInstanceRouteRef}
            params={{
              workflow: extractWorkflowFromInstanceName(item.name) || '',
            }}
          >
            {item.displayName}
          </ResourceLink>
        )}
        renderItem={WorkflowInstanceItem}
        loading={loading}
      />
      {children}
    </>
  );
};
